/**
 * useAutoHideScrollbars — calendar-style auto-hide scrollbars for the canonical
 * `.xv-scroll` utility (common/styles/scrollbars.css): invisible at rest, the
 * #9aa0a6 thumb appears while scrolling and fades after a short idle. Works
 * identically on every OS/theme instead of relying on the macOS overlay scrollbar.
 *
 * How it works: scroll events don't bubble but ARE capturable, so a single
 * capture-phase listener toggles `.is-scrolling` on whichever descendant actually
 * scrolled (`e.target`), with a per-element idle timer. The matching CSS lives in
 * common/styles/scrollbars.css (the `.xv-scroll` block) — webkit scrollbar pseudos
 * can't transition, so the show/hide is a background swap keyed on the class.
 *
 * Two entry points:
 *   useAutoHideScrollbars()       → returns an onScrollCapture handler to attach on
 *                                   a subtree root (e.g. a module root). Scoped.
 *   useGlobalAutoHideScrollbars() → attaches ONE document-level capture listener so
 *                                   the toggle works app-wide, including content
 *                                   rendered through portals (modals, dropdowns,
 *                                   popovers). Mount once near the app shell root.
 *
 * Only elements that opt into the `.xv-scroll` CSS visibly react; the class on any
 * other scrolled element is a harmless no-op.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { UIEvent } from 'react';

const SCROLL_IDLE_MS = 700;

type TimerMap = Map<HTMLElement, ReturnType<typeof setTimeout>>;

/** Add `.is-scrolling` to the scrolled element and arm/extend its idle timer. */
function markScrolling(scroller: HTMLElement, timers: TimerMap): void {
  scroller.classList.add('is-scrolling');
  const existing = timers.get(scroller);
  if (existing) clearTimeout(existing);
  timers.set(
    scroller,
    setTimeout(() => {
      scroller.classList.remove('is-scrolling');
      timers.delete(scroller);
    }, SCROLL_IDLE_MS),
  );
}

/**
 * Returns a handler to attach as `onScrollCapture` on a subtree root. The handler
 * lives on the root via capture, but the class belongs on the element that
 * actually scrolled (`e.target`, not `currentTarget`).
 */
export function useAutoHideScrollbars() {
  const timersRef = useRef<TimerMap>(new Map());

  const handleScrollCapture = useCallback((e: UIEvent<HTMLElement>) => {
    const scroller = e.target;
    if (!(scroller instanceof HTMLElement)) return;
    markScrolling(scroller, timersRef.current);
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  return handleScrollCapture;
}

/**
 * Attaches ONE capture-phase scroll listener on `document` so `.is-scrolling` is
 * toggled for every scroll in the app — including portal content (modals,
 * dropdowns, popovers) that lives outside the React subtree. Mount once at the app
 * shell root (see AppContent in app/App.tsx). No-op for non-element scroll targets
 * (e.g. the document itself).
 */
export function useGlobalAutoHideScrollbars(): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const timers: TimerMap = new Map();
    const onScroll = (e: Event): void => {
      const scroller = e.target;
      if (!(scroller instanceof HTMLElement)) return;
      markScrolling(scroller, timers);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions);
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);
}
